import { api, thunks } from "@app/api";
import { createSelector, select } from "@app/fx";
import { extractIdFromLink } from "@app/hal";
import { defaultEntity } from "@app/hal";
import { type WebState, defaultDeployLlmPolicy, schema } from "@app/schema";
import type {
  DeployLlmPolicy,
  DeployLlmPolicyResponse,
  HalEmbedded,
} from "@app/types";

type LlmPoliciesResponse = HalEmbedded<{
  llm_policies: DeployLlmPolicyResponse[];
}>;

export const deserializeLlmPolicy = (
  payload: DeployLlmPolicyResponse,
): DeployLlmPolicy => {
  // Special handling for the usage object, which could be null in the case of
  // an upstream error between deploy-api and the LLM gateway.
  const u = payload.current_usage;
  const spend = u?.spend ?? 0;
  const inputTokens = u?.input_tokens ?? 0;
  const outputTokens = u?.output_tokens ?? 0;
  const successfulRequests = u?.successful_requests ?? 0;
  const failedRequests = u?.failed_requests ?? 0;
  const totalTokens = inputTokens + outputTokens;
  const totalRequests = successfulRequests + failedRequests;

  return {
    id: `${payload.id}`,
    spendLimit: payload.spend_limit,
    environmentId: extractIdFromLink(payload._links?.account),
    models: Array.isArray(payload.models) ? payload.models : [],
    currentUsage: {
      spend,
      inputTokens,
      outputTokens,
      successfulRequests,
      failedRequests,
      totalRequests,
      totalTokens,
    },
  };
};

export const selectLlmPolicyById = schema.llmPolicies.selectById;
export const selectLlmPoliciesAsList = schema.llmPolicies.selectTableAsList;
export const selectLlmPoliciesByEnvId = createSelector(
  selectLlmPoliciesAsList,
  (_: WebState, props: { envId: string }) => props.envId,
  (policies, envId) =>
    policies.filter((policy) => policy.environmentId === envId),
);

export const selectOrgAiUsageTotals = createSelector(
  selectLlmPoliciesAsList,
  (policies) => {
    const totals = {
      totalSpend: 0,
      totalInputTokens: 0,
      totalOutputTokens: 0,
      totalTokens: 0,
      totalSuccessfulRequests: 0,
      totalFailedRequests: 0,
      totalRequests: 0,
    };

    return policies.reduce((acc, policy) => {
      const u = policy.currentUsage;
      acc.totalSpend += u.spend;
      acc.totalInputTokens += u.inputTokens;
      acc.totalOutputTokens += u.outputTokens;
      acc.totalTokens += u.totalTokens;
      acc.totalSuccessfulRequests += u.successfulRequests;
      acc.totalFailedRequests += u.failedRequests;
      acc.totalRequests += u.totalRequests;
      return acc;
    }, totals);
  },
);

export const fetchLlmPolicies = api.get<{ orgId: string }, LlmPoliciesResponse>(
  "/llm_policies?per_page=5000&organization_id=:orgId&usage=true",
  function* (ctx, next) {
    const { orgId } = ctx.payload;
    if (!orgId) {
      return;
    }

    yield* next();
    if (!ctx.json.ok) {
      return;
    }

    yield* schema.update(schema.llmPolicies.reset());
  },
);

export const fetchLlmPolicy = api.get<
  { envId: string; orgId: string },
  LlmPoliciesResponse
>(
  "/accounts/:envId/llm_policies?organization_id=:orgId&usage=true",
  function* (ctx, next) {
    const { envId, orgId } = ctx.payload;
    if (!envId || !orgId) {
      return;
    }

    yield* next();
    if (!ctx.json.ok) {
      return;
    }

    const llmPolicies = ctx.json.value._embedded.llm_policies;

    if (!llmPolicies.length) {
      return defaultDeployLlmPolicy({ environmentId: ctx.payload.envId });
    }

    const policy = {
      ...deserializeLlmPolicy(llmPolicies[0]),
      environmentId: ctx.payload.envId,
    };

    yield* schema.update(schema.llmPolicies.add({ [policy.id]: policy }));
  },
);

export interface UpdateLlmPolicy {
  envId: string;
  orgId: string;
  spendLimit?: number | null;
  models?: string[];
  silent?: boolean;
}

export const updateLlmPolicy = api.post<UpdateLlmPolicy>(
  "/accounts/:envId/llm_policies/create_or_update?organization_id=:orgId",
  function* (ctx, next) {
    const { envId, orgId, spendLimit, models, silent } = ctx.payload;
    if (!envId || !orgId) {
      return;
    }

    ctx.request = ctx.req({
      body: JSON.stringify({
        spend_limit: spendLimit,
        models,
      }),
    });

    yield* next();
    if (!ctx.json.ok) {
      return;
    }

    if (!silent) {
      ctx.loader = { message: "Successfully updated LLM policy!" };
    }
  },
);

export const ensureLlmPolicy = thunks.create<{ envId: string; orgId: string }>(
  "ensureLlmPolicy",
  [
    function* (ctx) {
      const { envId, orgId } = ctx.payload;
      if (!envId || !orgId) {
        return;
      }

      yield* fetchLlmPolicy.run({ envId, orgId });

      const policies = yield* select(selectLlmPoliciesByEnvId, { envId });
      if (!policies[0]?.id) {
        yield* updateLlmPolicy.run({
          envId,
          orgId,
          silent: true,
        });
        yield* fetchLlmPolicy.run({ envId, orgId });
      }
    },
  ],
);

export const llmPolicyEntities = {
  llm_policy: defaultEntity({
    id: "llm_policy",
    deserialize: deserializeLlmPolicy,
    save: schema.llmPolicies.add,
  }),
};
